import React, { createContext, ReactNode, useEffect } from 'react';
import useInvoicesController from '../components/Page/Exports/Invoices/useInvoicesController';
import useTaxesController from '../components/Page/Exports/Taxes/useTaxesController';
import useCompaniesController from '../components/Page/Exports/Companies/useCompaniesController';
import useDepositsController from '../components/Page/Exports/Deposits/useDepositsController';
import useStore from '../components/Hooks/useStore';
import Modal from '../components/Modal';
import useLogisticsController from '../components/Page/Exports/Logistic/useLogisticsController';

type ExportContextType = {
  controllers: {
    invoice: ReturnType<typeof useInvoicesController>;
    tax: ReturnType<typeof useTaxesController>;
    company: ReturnType<typeof useCompaniesController>;
    deposit: ReturnType<typeof useDepositsController>;
    logistic: ReturnType<typeof useLogisticsController>;
  };
};

export const ExportContext = createContext<ExportContextType | null>(null);

type ExportProviderProps = {
  children: ReactNode;
};

export const ExportProvider: React.FC<ExportProviderProps> = ({ children }) => {
  const [getters] = useStore();

  const {
    modal: { getModal },
  } = getters;

  const invoicesController = useInvoicesController();
  const taxesController = useTaxesController();
  const companiesController = useCompaniesController();
  const depositsController = useDepositsController();
  const logisticsController = useLogisticsController();

  const { searchFilter: invoiceSearchFilter, filterInvoice } =
    invoicesController;
  const { searchFilter: taxSearchFilter, filterTax } = taxesController;
  const { searchFilter: companySearchFilter, filterCompany } =
    companiesController;
  const { searchFilter: depositSearchFilter, filterDeposit } =
    depositsController;
  const { 
    logisticSearchFilter,
    filterLogistic,
    logisticOriginSearchFilter,
    filterLogisticOrigin,
   } = logisticsController;

  let invoiceSearchTimerId: NodeJS.Timeout;
  useEffect(() => {
    const handleInputChange = () => {
      clearTimeout(invoiceSearchTimerId);
      invoiceSearchTimerId = setTimeout(() => {
        filterInvoice();
      }, 500);
    };
    handleInputChange();
    return () => {
      clearTimeout(invoiceSearchTimerId);
    };
  }, [invoiceSearchFilter]);

  let taxSearchTimerId: NodeJS.Timeout;
  useEffect(() => {
    const handleInputChange = () => {
      clearTimeout(taxSearchTimerId);
      taxSearchTimerId = setTimeout(() => {
        filterTax();
      }, 500);
    };
    handleInputChange();
    return () => {
      clearTimeout(taxSearchTimerId);
    };
  }, [taxSearchFilter]);

  let logisticSearchTimerId: NodeJS.Timeout;
  useEffect(() => {
    const handleInputChange = () => {
      clearTimeout(logisticSearchTimerId);
      logisticSearchTimerId = setTimeout(() => {
        filterLogistic();
      }, 500);
    };
    handleInputChange();
    return () => {
      clearTimeout(logisticSearchTimerId);
    };
  }, [logisticSearchFilter]);

  let logisticOriginSearchTimerId: NodeJS.Timeout;
  useEffect(() => {
    const handleInputChange = () => {
      clearTimeout(logisticOriginSearchTimerId);
      logisticOriginSearchTimerId = setTimeout(() => {
        filterLogisticOrigin();
      }, 500);
    };
    handleInputChange();
    return () => {
      clearTimeout(logisticOriginSearchTimerId);
    };
  }, [logisticOriginSearchFilter]);

  let companySearchTimerId: NodeJS.Timeout;
  useEffect(() => {
    const handleInputChange = () => {
      clearTimeout(companySearchTimerId);
      companySearchTimerId = setTimeout(() => {
        filterCompany();
      }, 500);
    };
    handleInputChange();
    return () => {
      clearTimeout(companySearchTimerId);
    };
  }, [companySearchFilter]);

  let depositSearchTimerId: NodeJS.Timeout;
  useEffect(() => {
    const handleInputChange = () => {
      clearTimeout(depositSearchTimerId);
      depositSearchTimerId = setTimeout(() => {
        filterDeposit();
      }, 500);
    };
    handleInputChange();
    return () => {
      clearTimeout(depositSearchTimerId);
    };
  }, [depositSearchFilter]);

  return (
    <ExportContext.Provider
      value={{
        controllers: {
          invoice: invoicesController,
          tax: taxesController,
          company: companiesController,
          deposit: depositsController,
          logistic: logisticsController,
        },
      }}
    >
      {children}
      {getModal('single.createInvoice') ? (
        <Modal.Export.Invoice.Create />
      ) : null}
      {getModal('single.updateInvoice') ? (
        <Modal.Export.Invoice.Update />
      ) : null}
      {getModal('single.createTax') ? <Modal.Export.Tax.Create /> : null}
      {getModal('single.updateTax') ? <Modal.Export.Tax.Update /> : null}
      {getModal('single.createCompany') ? (
        <Modal.Export.Company.Create />
      ) : null}
      {getModal('single.createDeposit') ? (
        <Modal.Export.Deposit.Create />
      ) : null}
      {getModal('single.updateCompany') ? (
        <Modal.Export.Company.Update />
      ) : null}
      {getModal('single.updateDeposit') ? (
        <Modal.Export.Deposit.Update />
      ) : null}
      {getModal('single.filterCompany') ? (
        <Modal.Export.Company.Filter />
      ) : null}
      {getModal('single.filterInvoice') ? (
        <Modal.Export.Invoice.Filter />
      ) : null}
      {getModal('single.filterTax') ? <Modal.Export.Tax.Filter /> : null}
      {getModal('single.filterDeposit') ? (
        <Modal.Export.Deposit.Filter />
      ) : null}
      {getModal('single.deleteCompanies') ? (
        <Modal.Export.Company.Delete />
      ) : null}
      {getModal('single.deleteTaxes') ? <Modal.Export.Tax.Delete /> : null}
      {getModal('single.deleteInvoices') ? (
        <Modal.Export.Invoice.Delete />
      ) : null}
      {getModal('single.deleteDeposits') ? (
        <Modal.Export.Deposit.Delete />
      ) : null}
      {getModal('single.updateManyDeposits') ? (
        <Modal.Export.Deposit.UpdateMany />
      ) : null}
      {getModal('single.createLogistic') ? <Modal.Export.Logistic.Create /> : null}
      {getModal('single.updateLogistic') ? <Modal.Export.Logistic.Update /> : null}
      {getModal('single.deleteLogistics') ? <Modal.Export.Logistic.Delete /> : null}
      {getModal('single.filterLogistic') ? <Modal.Export.Logistic.Filter /> : null}
      {getModal('single.createLogisticOrigin') ? <Modal.Export.Logistic.Origin.Create /> : null}
      {getModal('single.updateLogisticOrigin') ? <Modal.Export.Logistic.Origin.Update /> : null}
      {getModal('single.deleteLogisticOrigins') ? <Modal.Export.Logistic.Origin.Delete /> : null}
    </ExportContext.Provider>
  );
};
